import React from 'react';

import { LogIn } from 'lucide-react';

import { Badge } from '../../components/common/Badge';
import Table from '../../components/common/Table';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge } from '../../utils/displayUtils';

interface MemberTableProps {
  data: any[];
  handleEdit: (service: any) => void;
  handleDelete: (service: any) => void;
  handleImpersonateLogin: (service: any) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
}

const MemberTable: React.FC<MemberTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  handleImpersonateLogin,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
}) => (
  <Table
    columns={[
      {
        label: 'First Name',
        key: 'firstName',
        sortable: true,
        sortKey: 'FirstName',
      },
      {
        label: 'Last Name',
        key: 'lastName',
        sortable: true,
        sortKey: 'LastName',
      },
      {
        label: 'Email',
        key: 'email',
        sortable: true,
        sortKey: 'Email',
      },
      {
        label: 'Phone',
        key: 'phone',
        sortable: true,
        sortKey: 'Phone',
      },
      {
        label: 'Membership',
        key: 'memberTypeName',
        sortable: true,
        sortKey: 'MemberType',
        render: (row: any) => row.memberTypeName ?? '-',
      },
      {
        label: 'Active',
        key: 'active',
        render: (row: any) => renderBooleanBadge(row.active ?? false),
      },
      {
        // Null means they have never signed in to the new app (most migrated members). The date
        // is the last sign-in, so an admin can also see who has stopped coming back.
        label: 'Logged In',
        key: 'lastLoginUtc',
        sortable: true,
        sortKey: 'LastLoginUtc',
        render: (row: any) =>
          row.lastLoginUtc != null ? (
            <span title="Last sign-in to the new app">
              {convertToUserTime(
                new Date(row.lastLoginUtc).toISOString(),
                true,
              )}
            </span>
          ) : (
            <Badge label="Never" variant="brand" />
          ),
      },
    ]}
    data={data}
    actions={{
      onEdit: handleEdit,
      onDelete: handleDelete,
      extra: [
        {
          label: 'Login as member',
          icon: <LogIn />,
          confirmMessage: 'Are you sure you want to login as this member?',
          onClick: handleImpersonateLogin,
        },
      ],
    }}
    currentPage={currentPage}
    totalPages={totalPages}
    onPageChange={setCurrentPage}
    pageSize={pageSize}
    onPageSizeChange={setPageSize}
    isLoading={isLoading}
    sortBy={sortBy}
    sortDescending={sortDescending}
    onSort={onSort}
  />
);

export default MemberTable;
